import React, { useState, useEffect, useMemo } from "react";
import {
    Box, Paper, Typography, TextField, Button, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Grid, Tooltip, IconButton, Alert
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { API_BASE } from "../../apiBase";

function useAuthHeaders() {
    const token = localStorage.getItem("token");
    return useMemo(() => ({
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
    }), [token]);
}

const ENTITY_TYPES = [
    "User", "Tool", "ToolExecution", "TestSuite", "TestDocument", "ApiKey"
];

export default function AuditLogsSubTab() {
    const headers = useAuthHeaders();

    // Filters
    const [entityType, setEntityType] = useState("");
    const [userId, setUserId] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [limit, setLimit] = useState(100);

    // Data
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Fetch logs
    const fetchLogs = async () => {
        try {
            setLoading(true);
            setError("");

            const params = new URLSearchParams();
            if (entityType) params.append("entity_type", entityType);
            if (userId) params.append("user_id", userId);
            if (startDate) params.append("start_date", new Date(startDate).toISOString());
            if (endDate) params.append("end_date", new Date(endDate).toISOString());
            params.append("limit", limit);

            const res = await fetch(`${API_BASE}/admin/audit/deletions?${params.toString()}`, {
                headers
            });

            if (!res.ok) {
                throw new Error(`Failed to fetch audit logs: ${res.status}`);
            }

            const data = await res.json();
            setLogs(data.deletions || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Load on mount
    useEffect(() => {
        fetchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    // Get action color
    const getActionColor = (action) => {
        if (action.includes('deleted')) return 'error';
        if (action.includes('cleanup')) return 'warning';
        if (action.includes('archived')) return 'info';
        return 'default';
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Audit Trail (ISDS A2-1.2.5)
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                Traceable records of data deletions, archiving, and cleanup operations for compliance verification.
            </Typography>

            {/* Summary Info */}
            <Alert severity="info" sx={{ mb: 3, mt: 2 }}>
                Audit logs are retained for 10 years and provide verifiable proof of deletion operations.
                All entries include who performed the action, when, and what was affected.
            </Alert>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Entity Type"
                            value={entityType}
                            onChange={(e) => setEntityType(e.target.value)}
                            placeholder="e.g., User, Tool"
                            fullWidth
                            size="small"
                            select
                            SelectProps={{ native: true }}
                        >
                            <option value="">All Types</option>
                            {ENTITY_TYPES.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="User ID"
                            type="number"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Start Date"
                            type="datetime-local"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="End Date"
                            type="datetime-local"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Box display="flex" gap={1}>
                            <Button
                                variant="contained"
                                onClick={fetchLogs}
                                disabled={loading}
                                fullWidth
                            >
                                {loading ? <CircularProgress size={20} /> : "Search"}
                            </Button>
                            <Tooltip title="Refresh">
                                <IconButton onClick={fetchLogs} disabled={loading}>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            {/* Logs Table */}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell>Entity Type</TableCell>
                            <TableCell>Entity ID</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>IP Address</TableCell>
                            <TableCell>Retention Until</TableCell>
                            <TableCell>Details</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && logs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : logs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No audit logs found. Adjust filters or wait for deletion/archiving operations.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            logs.map((log) => (
                                <TableRow key={log.id} hover>
                                    <TableCell>
                                        <Tooltip title={log.timestamp}>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                {formatTimestamp(log.timestamp)}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.action}
                                            size="small"
                                            color={getActionColor(log.action)}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                            {log.entity_type}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                            {log.entity_id || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={`User ID: ${log.user_id || 'SYSTEM'}`}>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                {log.username}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                            {log.ip_address || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="Logs retained until this date">
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                {log.retention_until ? formatTimestamp(log.retention_until) : '—'}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={JSON.stringify(log.details, null, 2)}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    maxWidth: '200px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {log.details?.entity_name || log.details?.reason || 'View details'}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {logs.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {logs.length} audit entries (limit: {limit})
                    </Typography>
                    {logs.length >= limit && (
                        <Typography variant="body2" color="warning.main">
                            Results limited. Increase limit or refine filters to see more.
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}
